import React, { useState, useEffect } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import { FaMars, FaVenus, FaGenderless } from 'react-icons/fa';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import maleOption from '../../assets/images/male-option.png';
import femaleOption from '../../assets/images/female-option.png';
import nonBinaryOption from '../../assets/images/non-binary-option.png';
import AdvancedFilters from '../AdvancedFilters/AdvancedFilters';
import './BasicFilters.css';
import { useGoogleTranslate } from '../../Hooks/useGoogleTranslate';

const BasicFilters = ({ onApply, onClear, initialFilters = {} }) => {
  const basicFilterTitle = useGoogleTranslate('Basic Filter');
  const advancedFilterTitle = useGoogleTranslate('Advanced Filter');
  const basicTabLabel = useGoogleTranslate('Basic Filter');
  const advancedTabLabel = useGoogleTranslate('Advanced Filter');
  const genderLabel = useGoogleTranslate('I am looking for');
  const maleLabel = useGoogleTranslate('Men');
  const femaleLabel = useGoogleTranslate('Women');
  const nonBinaryLabel = useGoogleTranslate('Non Binary');
  const distanceLabel = useGoogleTranslate('Distance');
  const milesLabel = useGoogleTranslate('Miles');
  const ageBetweenLabel = useGoogleTranslate('Age Between 18 to 90');
  const clearLabel = useGoogleTranslate('Clear');
  const applyLabel = useGoogleTranslate('Apply');
  const idealMatchHeading = useGoogleTranslate('What is your ideal match?');
  const [activeTab, setActiveTab] = useState('basic');
  const [basicFilters, setBasicFilters] = useState({
    gender: initialFilters.gender || '',
    ageMin: initialFilters.age_min || 18,
    ageMax: initialFilters.age_max || 35,
    distance: initialFilters.distance || 50,
  });

  const handleGenderSelect = (gender) => {
    setBasicFilters(prev => ({ ...prev, gender }));
  };

  const handleAgeRangeChange = (values) => {
    setBasicFilters(prev => ({
      ...prev,
      ageMin: values[0],
      ageMax: values[1]
    }));
  };

  const handleDistanceChange = (value) => {
    setBasicFilters(prev => ({ ...prev, distance: value }));
  };

  const handleBasicApply = () => {
    const filterParams = {
      gender: basicFilters.gender,
      age_min: basicFilters.ageMin,
      age_max: basicFilters.ageMax,
      distance: basicFilters.distance
    };
    onApply(filterParams);
  };

  const handleBasicClear = () => {
    const defaultFilters = {
      gender: '',
      ageMin: 18,
      ageMax: 35,
      distance: 50
    };
    setBasicFilters(defaultFilters);

    // Clear with new format
    const clearedParams = {
      gender: {
        data: [],
        ran_out: false
      },
      age: {
        min: 18,
        max: 35
      },
      location: {
        distance: 50
      }
    };
    onClear(clearedParams);
  };

  const handleAdvancedApply = (advancedFilters) => {
    // Combine basic filters with advanced filters
    const combinedFilters = {
      gender: {
        data: basicFilters.gender ? [basicFilters.gender] : [],
        ran_out: false
      },
      age: {
        min: basicFilters.ageMin,
        max: basicFilters.ageMax
      },
      location: {
        distance: basicFilters.distance
      },
      ...advancedFilters
    };
    onApply(combinedFilters);
  };

  const handleAdvancedClear = () => {
    const defaultFilters = {
      gender: '',
      ageMin: 18,
      ageMax: 35,
      distance: 50
    };
    setBasicFilters(defaultFilters);
    onClear(defaultFilters);
  };

  return (
    <div className="">
      <div className="filter-header text-center">
        <h4>{activeTab === 'basic' ? basicFilterTitle : advancedFilterTitle}</h4>
      </div >

      {/* Filter Type Selector */}
      < div className="filter-type-selector" >
        <div className="filter-tabs">
          <button
            className={`filter-tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            {basicTabLabel}
          </button>
          <button
            className={`filter-tab ${activeTab === 'advanced' ? 'active' : ''}`}
            onClick={() => setActiveTab('advanced')}
          >
            {advancedTabLabel}
          </button>
        </div>
      </div >

      {activeTab === 'basic' ? (
        <>
          {/* Ideal Match Heading */}
          <div className="ideal-match-heading text-center mb-4">
            <h4 className="text-dark fw-bold">{idealMatchHeading}</h4>
          </div>

          {/* Gender Filter */}
          <div className="filter-section">
            <h5 className="filter-label">{genderLabel}</h5>
            <div className="gender-options">
              <div
                className={`gender-option male-option ${basicFilters.gender === 'man' ? 'active' : ''}`}
                onClick={() => handleGenderSelect('man')}
              >
                <div className="gender-option-picture">
                  <div className="gender-icon">
                    <FaMars size={20} />
                  </div>
                  <img src={maleOption} alt="Male" className="img-fluid" />
                </div>
                <span className="gender-title">{maleLabel}</span>
              </div >
              <div
                className={`gender-option female-option ${basicFilters.gender === 'woman' ? 'active' : ''}`}
                onClick={() => handleGenderSelect('woman')}
              >
                <div className="gender-option-picture">
                  <div className="gender-icon">
                    <FaVenus size={20} />
                  </div>
                  <img src={femaleOption} alt="Female" className="img-fluid" />
                </div>
                <span className="gender-title">{femaleLabel}</span>
              </div >
              <div
                className={`gender-option non-binary-option ${basicFilters.gender === 'non-binary' ? 'active' : ''}`}
                onClick={() => handleGenderSelect('non-binary')}
              >
                <div className="gender-option-picture">
                  <div className="gender-icon">
                    <FaGenderless size={20} />
                  </div>
                  <img src={nonBinaryOption} alt="Non Binary" className="img-fluid" />
                </div>
                <span className="gender-title">{nonBinaryLabel}</span>
              </div >
            </div >
          </div >

          {/* Distance Filter */}
          < div className="filter-section" >
            <h5 className="filter-label">{distanceLabel}</h5>
            <div className="distance-slider">
              <div className="distance-display">
                <span>
                  {basicFilters.distance} {milesLabel}
                </span>
              </div>
              <Slider
                min={0}
                max={250}
                value={basicFilters.distance}
                onChange={handleDistanceChange}
                styles={{
                  rail: {
                    backgroundColor: '#E0E0E0',
                    height: 8
                  },
                  track: {
                    backgroundColor: '#5494DA',
                    height: 8
                  },
                  handle: {
                    borderColor: '#5494DA',
                    backgroundColor: '#5494DA',
                    opacity: 1,
                    boxShadow: 'none',
                    width: 20,
                    height: 20,
                    marginTop: -6,
                    border: 'none'
                  }
                }}
              />
            </div>
          </div>

          {/* Age Filter */}
          <div className="filter-section">
            <h5 className="filter-label">{ageBetweenLabel}</h5>
            <div className="age-slider">
              <div className="age-range-display">
                {basicFilters.ageMin} - {basicFilters.ageMax}
              </div>
              <Slider
                range
                min={18}
                max={90}
                value={[basicFilters.ageMin, basicFilters.ageMax]}
                onChange={handleAgeRangeChange}
                styles={{
                  rail: {
                    backgroundColor: '#E0E0E0',
                    height: 8
                  },
                  track: {
                    backgroundColor: '#5494DA',
                    height: 8
                  },
                  handle: {
                    borderColor: '#5494DA',
                    backgroundColor: '#5494DA',
                    opacity: 1,
                    boxShadow: 'none',
                    width: 20,
                    height: 20,
                    marginTop: -6,
                    border: 'none'
                  }
                }}
              />
            </div>
          </div >

          {/* Action Buttons */}
          < div className="filter-actions" >
            <Button
              variant="outline-primary"
              className="clear-btn"
              onClick={handleBasicClear}
            >
              {clearLabel}
            </Button>
            <Button
              variant="primary"
              className="apply-btn"
              onClick={handleBasicApply}
            >
              {applyLabel}
            </Button>
          </div>
        </>
      ) : (
        <AdvancedFilters
          onApply={handleAdvancedApply}
          onClear={handleAdvancedClear}
          initialFilters={initialFilters}
        />
      )}
    </div >
  );
};

export default BasicFilters;
